import { useState } from "react";
import { useAppPreferences, type TranslationFunction } from "../../lib/preferences";
import {
  ProviderNotificationInboxView,
  type ProviderNotificationCategory,
  type ProviderNotificationCategoryFilter,
  type ProviderNotificationInboxState,
  type ProviderNotificationPaginationState,
  type ProviderNotificationPreferencesBoundary,
  type ProviderNotificationViewItem,
} from "./ProviderNotificationInboxView";
import type { ProviderNotificationItem } from "./useProviderNotifications";
import { mapProviderNotificationToViewItem, type ProviderNotificationServerItem } from "./provider-notification-data";

function isServerNotification(item: ProviderNotificationItem): item is ProviderNotificationServerItem {
  return "dto" in item;
}

function getNotificationTitle(item: Exclude<ProviderNotificationItem, ProviderNotificationServerItem>, t: TranslationFunction) {
  const name = item.customerName;
  switch (item.type) {
    case "booking.created":
      return name ? t("New booking from {name}", { name }) : t("New booking");
    case "booking.updated":
      return name ? t("Booking updated for {name}", { name }) : t("Booking updated");
    case "session.started":
      return name ? t("Session started for {name}", { name }) : t("Session started");
    case "session.updated":
      return name ? t("Session updated for {name}", { name }) : t("Session updated");
    case "session.stopped":
      return name ? t("Session ended for {name}", { name }) : t("Session ended");
    case "food_order.created":
      return t("New food order");
    case "food_order.updated":
      return t("Food order updated");
    default:
      return t("Notifications");
  }
}

export interface ProviderNotificationsPanelProps {
  readBusy?: boolean;
  readError?: boolean;
  notifications: ProviderNotificationItem[];
  activeCategory?: ProviderNotificationCategoryFilter;
  inboxState?: ProviderNotificationInboxState;
  onCategoryChange?: (category: ProviderNotificationCategoryFilter) => void;
  onDismissPending: () => void;
  onLoadMore?: (cursor: string) => void;
  onMarkAllRead: () => void;
  onMarkNotificationRead?: (notificationId: string) => void;
  onOpenNotification: (item: ProviderNotificationItem) => void;
  onRefresh?: () => void;
  onReviewPending: () => void;
  pendingCount: number;
  pendingUnread: boolean;
  pagination?: ProviderNotificationPaginationState;
  preferences?: ProviderNotificationPreferencesBoundary;
  unreadCount: number;
}

/**
 * Docked notification list for the provider shell. Replaces the old fixed
 * floating alert: the pending-confirmations alert is pinned as the first row
 * and realtime booking/session/food-order events stream in underneath. All
 * state and deep-link behavior is owned by ProviderLayout; this component is
 * presentation only, rendered in the desktop sidebar and (toggled) in the
 * mobile header.
 */
export function ProviderNotificationsPanel({
  notifications,
  readBusy,
  readError,
  activeCategory: controlledCategory,
  inboxState = { status: "ready" },
  onCategoryChange,
  onDismissPending,
  onLoadMore = () => undefined,
  onMarkAllRead,
  onMarkNotificationRead,
  onOpenNotification,
  onRefresh,
  onReviewPending,
  pendingCount,
  pendingUnread,
  pagination = { nextCursor: null, status: "idle" },
  preferences,
  unreadCount,
}: ProviderNotificationsPanelProps) {
  const { language, t } = useAppPreferences();
  const [localCategory, setLocalCategory] = useState<ProviderNotificationCategoryFilter>("all");
  const activeCategory = controlledCategory ?? localCategory;

  const viewItems: ProviderNotificationViewItem[] = notifications.map((notification) =>
    isServerNotification(notification)
      ? mapProviderNotificationToViewItem(notification, t, language)
      : {
          category: legacyCategory(notification),
          createdAtMs: notification.createdAtMs,
          id: notification.id,
          read: notification.read,
          superseded: notification.muted,
          target: { availability: "available", kind: legacyTargetKind(notification) },
          title: getNotificationTitle(notification, t),
        },
  );
  const legacyItemsById = new Map(notifications.map((notification) => [notification.id, notification]));

  return (
    <ProviderNotificationInboxView
      initialUnreadOnly
      readBusy={readBusy}
      readError={readError}
      activeCategory={activeCategory}
      inboxState={inboxState}
      items={viewItems}
      language={language}
      onCategoryChange={(category) => {
        setLocalCategory(category);
        onCategoryChange?.(category);
      }}
      onLoadMore={onLoadMore}
      onMarkAllRead={onMarkAllRead}
      onMarkItemRead={onMarkNotificationRead}
      onOpenItem={(item) => {
        const notification = legacyItemsById.get(item.id);
        if (notification) onOpenNotification(notification);
      }}
      onRefresh={onRefresh}
      pagination={pagination}
      pendingConfirmation={
        pendingCount > 0
          ? {
              count: pendingCount,
              onMarkRead: onDismissPending,
              onReview: onReviewPending,
              unread: pendingUnread,
            }
          : undefined
      }
      preferences={preferences}
      unreadCount={unreadCount}
    />
  );
}

function legacyCategory(notification: Exclude<ProviderNotificationItem, ProviderNotificationServerItem>): ProviderNotificationCategory {
  if (notification.type.startsWith("booking.")) return "booking";
  if (notification.type.startsWith("session.")) return "session";
  return "food_order";
}

function legacyTargetKind(notification: Exclude<ProviderNotificationItem, ProviderNotificationServerItem>) {
  const category = legacyCategory(notification);
  if (category === "booking") return "booking" as const;
  if (category === "session") return "session" as const;
  return "food-order" as const;
}
